import type { JSX } from "preact";
import { classNames } from "@gss/common/classNames";
import { Button } from "@gss/components/Button";
import { ButtonGroup } from "@gss/components/ButtonGroup";
import { ButtonHelp } from "./ButtonHelp";
import "./NavButtons.css";

export interface NavButton<T extends number> {
    id: T;
    title: string;
    icon: JSX.ElementType;
}

interface Props<T extends number> {
    current?: number;
    className?: string;
    helpText?: string;
    buttons?: NavButton<T>[];
    onClick?: (button: NavButton<T>) => void;
}

export function NavButtons<T extends number>({
    className,
    helpText,
    current,
    buttons,
    onClick,
}: Props<T>): JSX.Element {
    return (
        <div className={classNames("nav-buttons text-end", className)}>
            {helpText != null && (
                <ButtonGroup small className="btn-group-help me-2">
                    <ButtonHelp popover={helpText} />
                </ButtonGroup>
            )}
            {buttons != null && (
                <ButtonGroup small>
                    {buttons.map((b) => {
                        const Icon = b.icon;
                        const isCurrent = current === b.id;
                        return (
                            <Button
                                key={b.id}
                                variant={isCurrent ? "primary" : "default"}
                                title={b.title}
                                aria-label={b.title}
                                aria-pressed={isCurrent}
                                onClick={() => onClick?.(b)}
                            >
                                <Icon aria-hidden />
                            </Button>
                        );
                    })}
                </ButtonGroup>
            )}
        </div>
    );
}
